import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import SeatMapView from "@/components/SeatMapView";
import ZButton from "@/components/ZButton";
import { ZInput, ZSelect } from "@/components/ZInput";
import Modal from "@/components/Modal";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Grid3x3, Layout, Save } from "lucide-react";

const COLORS = ["#01696F", "#DA7101", "#A12C7B", "#3B82F6", "#10B981", "#EF4444", "#F59E0B", "#6366F1"];

function uuid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function buildSeatGrid(zone, rows, cols) {
  const { x, y, width, height } = zone.shape;
  const padX = 30, padY = 30;
  const usableW = Math.max(40, width - padX * 2);
  const usableH = Math.max(40, height - padY * 2);
  const stepX = cols > 1 ? usableW / (cols - 1) : 0;
  const stepY = rows > 1 ? usableH / (rows - 1) : 0;
  const seats = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      seats.push({
        id: uuid(),
        label: `${String.fromCharCode(65 + r)}${c + 1}`,
        x: x + padX + c * stepX,
        y: y + padY + r * stepY,
      });
    }
  }
  return seats;
}

export default function OrgSeatMapPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [map, setMap] = useState({ mode: "zones", width: 800, height: 500, stage: { x: 300, y: 20, width: 200, height: 50, label: "SCÈNE" }, zones: [] });
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAddZone, setShowAddZone] = useState(false);
  const [zoneForm, setZoneForm] = useState({ name: "", category_id: "", color: COLORS[0], x: 100, y: 100, width: 300, height: 100, rows: 0, cols: 0 });
  const [zoneErr, setZoneErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [eR, mR] = await Promise.all([
          api.get(`/events/${id}`),
          api.get(`/events/${id}/seat-map`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setEvent(eR.data);
        if (mR.data) setMap(mR.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const cats = (event?.categories || []).filter((c) => c.id !== "legacy");

  const selectedZone = map.zones.find((z) => z.id === selectedZoneId);

  const openAddZone = () => {
    setZoneErr("");
    setZoneForm({
      name: "",
      category_id: cats[0]?.id || "",
      color: COLORS[map.zones.length % COLORS.length],
      x: 80, y: 100 + map.zones.length * 40, width: 380, height: 100,
      rows: 0, cols: 0,
    });
    setShowAddZone(true);
  };

  const addZone = () => {
    setZoneErr("");
    if (!zoneForm.name.trim()) return setZoneErr("Nom de zone obligatoire.");
    if (!zoneForm.category_id) return setZoneErr("Catégorie obligatoire.");
    const newZone = {
      id: uuid(),
      name: zoneForm.name.trim(),
      category_id: zoneForm.category_id,
      color: zoneForm.color,
      shape: {
        type: "rect",
        x: parseFloat(zoneForm.x) || 0,
        y: parseFloat(zoneForm.y) || 0,
        width: parseFloat(zoneForm.width) || 100,
        height: parseFloat(zoneForm.height) || 100,
      },
      seats: [],
    };
    if (map.mode === "seats" && zoneForm.rows > 0 && zoneForm.cols > 0) {
      newZone.seats = buildSeatGrid(newZone, parseInt(zoneForm.rows), parseInt(zoneForm.cols));
    }
    setMap({ ...map, zones: [...map.zones, newZone] });
    setSelectedZoneId(newZone.id);
    setShowAddZone(false);
  };

  const updateZone = (zoneId, patch) => {
    setMap({ ...map, zones: map.zones.map((z) => z.id === zoneId ? { ...z, ...patch } : z) });
  };

  const updateZoneShape = (zoneId, patch) => {
    setMap({ ...map, zones: map.zones.map((z) => z.id === zoneId ? { ...z, shape: { ...z.shape, ...patch } } : z) });
  };

  const removeZone = (zoneId) => {
    if (!window.confirm("Supprimer cette zone ?")) return;
    setMap({ ...map, zones: map.zones.filter((z) => z.id !== zoneId) });
    setSelectedZoneId(null);
  };

  const regenSeats = (zoneId) => {
    const z = map.zones.find((zz) => zz.id === zoneId);
    if (!z) return;
    const rows = parseInt(prompt("Nombre de rangées ?", "5") || 0);
    const cols = parseInt(prompt("Nombre de places par rangée ?", "10") || 0);
    if (!(rows > 0 && cols > 0)) return;
    if (rows * cols > 200) return toast.error("Max 200 sièges par zone.");
    updateZone(zoneId, { seats: buildSeatGrid(z, rows, cols) });
  };

  const switchMode = (newMode) => {
    if (newMode === map.mode) return;
    if (newMode === "zones") {
      // Just clear all seats
      setMap({ ...map, mode: "zones", zones: map.zones.map((z) => ({ ...z, seats: [] })) });
    } else {
      setMap({ ...map, mode: "seats" });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        mode: map.mode,
        width: parseInt(map.width),
        height: parseInt(map.height),
        stage: map.stage,
        zones: map.zones.map((z) => ({
          id: z.id,
          category_id: z.category_id,
          name: z.name,
          color: z.color,
          shape: {
            type: "rect",
            x: z.shape.x,
            y: z.shape.y,
            width: z.shape.width,
            height: z.shape.height,
          },
          seats: map.mode === "seats" ? (z.seats || []) : [],
        })),
      };
      const r = await api.put(`/events/${id}/seat-map`, payload);
      setMap(r.data);
      toast.success("Plan de salle enregistré");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const removeAll = async () => {
    if (!window.confirm("Supprimer entièrement le plan de salle ?")) return;
    try {
      await api.delete(`/events/${id}/seat-map`);
      setMap({ mode: "zones", width: 800, height: 500, stage: { x: 300, y: 20, width: 200, height: 50, label: "SCÈNE" }, zones: [] });
      toast.success("Plan supprimé");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (!event) return <div className="z-container">Événement introuvable.</div>;
  if (cats.length === 0) {
    return (
      <div className="z-container" data-testid="seatmap-no-categories">
        <h1 className="z-page-title">Plan de salle</h1>
        <p>Vous devez d&apos;abord créer au moins une catégorie de billets.</p>
        <Link to={`/org/event/${id}`} className="z-btn z-btn-primary">Retour</Link>
      </div>
    );
  }

  const totalSeats = map.zones.reduce((sum, z) => sum + (z.seats?.length || 0), 0);
  const totalCap = cats.reduce((s, c) => s + (c.capacity || 0), 0);

  return (
    <div className="z-container" style={{ maxWidth: 1200 }} data-testid="org-seatmap-page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <Link to={`/org/event/${id}`} className="z-btn z-btn-ghost z-btn-sm"><ArrowLeft size={14} /> Retour</Link>
          <h1 className="z-page-title" style={{ margin: "8px 0 0" }}>Plan de salle</h1>
          <p className="z-page-sub" style={{ margin: 0 }}>{event.title}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <ZButton variant="outline" onClick={removeAll} data-testid="seatmap-delete-btn"><Trash2 size={14} /></ZButton>
          <ZButton onClick={save} loading={saving} data-testid="seatmap-save-btn"><Save size={14} /> Enregistrer</ZButton>
        </div>
      </div>

      {/* Mode toggle */}
      <div style={{ background: "var(--z-bg)", padding: 12, borderRadius: 12, margin: "16px 0", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <strong style={{ fontSize: 14 }}>Mode :</strong>
        <button onClick={() => switchMode("zones")} className={`z-chip ${map.mode === "zones" ? "z-chip-active" : ""}`} data-testid="seatmap-mode-zones">
          <Layout size={14} /> Zones (sans sièges)
        </button>
        <button onClick={() => switchMode("seats")} className={`z-chip ${map.mode === "seats" ? "z-chip-active" : ""}`} data-testid="seatmap-mode-seats">
          <Grid3x3 size={14} /> Sièges numérotés
        </button>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--z-muted)" }}>
          {map.mode === "seats" ? <>{totalSeats} sièges / {totalCap} places dispo</> : <>{map.zones.length} zone(s)</>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16, alignItems: "flex-start" }}>
        {/* Canvas */}
        <div className="z-card" style={{ padding: 12 }}>
          <SeatMapView
            map={map}
            mode="editor"
            selectedZoneId={selectedZoneId}
            onZoneClick={(zid) => setSelectedZoneId(zid)}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <ZButton size="sm" onClick={openAddZone} data-testid="seatmap-add-zone-btn"><Plus size={14} /> Ajouter une zone</ZButton>
            {selectedZone && map.mode === "seats" && (
              <ZButton size="sm" variant="outline" onClick={() => regenSeats(selectedZone.id)} data-testid="seatmap-gen-seats-btn">
                <Grid3x3 size={14} /> Générer sièges
              </ZButton>
            )}
          </div>
        </div>

        {/* Right panel */}
        <div className="z-card" style={{ padding: 14 }}>
          {!selectedZone ? (
            <div>
              <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Plan de salle</h3>
              <p style={{ fontSize: 13, color: "var(--z-muted)" }}>
                Cliquez sur une zone pour la modifier. Sélectionnez le mode {map.mode === "zones" ? "« Sièges numérotés » pour activer la sélection siège-par-siège" : "« Zones » pour désactiver les sièges"}.
              </p>
              <div style={{ marginTop: 14 }}>
                <label className="z-label" style={{ fontSize: 13 }}>Dimensions plan</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <ZInput label="Largeur" type="number" min="200" max="2000" value={map.width} onChange={(e) => setMap({ ...map, width: parseInt(e.target.value) || 800 })} data-testid="seatmap-width-input" />
                  <ZInput label="Hauteur" type="number" min="200" max="2000" value={map.height} onChange={(e) => setMap({ ...map, height: parseInt(e.target.value) || 500 })} data-testid="seatmap-height-input" />
                </div>
                <ZInput label="Texte de la scène" value={map.stage?.label || ""} onChange={(e) => setMap({ ...map, stage: { ...map.stage, label: e.target.value } })} data-testid="seatmap-stage-label" />
              </div>
            </div>
          ) : (
            <div data-testid={`seatmap-zone-panel-${selectedZone.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, fontSize: 15 }}>{selectedZone.name}</h3>
                <button onClick={() => removeZone(selectedZone.id)} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error,#c00)" }} data-testid={`seatmap-remove-zone-${selectedZone.id}`}><Trash2 size={14} /></button>
              </div>
              <ZInput label="Nom" value={selectedZone.name} onChange={(e) => updateZone(selectedZone.id, { name: e.target.value })} data-testid="seatmap-zone-name" />
              <ZSelect label="Catégorie" value={selectedZone.category_id} onChange={(e) => updateZone(selectedZone.id, { category_id: e.target.value })} data-testid="seatmap-zone-cat">
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.price})</option>)}
              </ZSelect>
              <label className="z-label" style={{ fontSize: 13 }}>Couleur</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {COLORS.map((c) => (
                  <button key={c} onClick={() => updateZone(selectedZone.id, { color: c })}
                    style={{ width: 28, height: 28, borderRadius: "50%", background: c, border: selectedZone.color === c ? "3px solid white" : "1px solid #ddd", boxShadow: selectedZone.color === c ? `0 0 0 2px ${c}` : "none", cursor: "pointer" }}
                    data-testid={`seatmap-color-${c}`} />
                ))}
              </div>
              <label className="z-label" style={{ fontSize: 13 }}>Position & taille</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <ZInput label="X" type="number" value={selectedZone.shape.x} onChange={(e) => updateZoneShape(selectedZone.id, { x: parseFloat(e.target.value) || 0 })} data-testid="seatmap-zone-x" />
                <ZInput label="Y" type="number" value={selectedZone.shape.y} onChange={(e) => updateZoneShape(selectedZone.id, { y: parseFloat(e.target.value) || 0 })} data-testid="seatmap-zone-y" />
                <ZInput label="Largeur" type="number" value={selectedZone.shape.width} onChange={(e) => updateZoneShape(selectedZone.id, { width: parseFloat(e.target.value) || 50 })} data-testid="seatmap-zone-w" />
                <ZInput label="Hauteur" type="number" value={selectedZone.shape.height} onChange={(e) => updateZoneShape(selectedZone.id, { height: parseFloat(e.target.value) || 50 })} data-testid="seatmap-zone-h" />
              </div>
              {map.mode === "seats" && (
                <p style={{ fontSize: 12, color: "var(--z-muted)", marginTop: 8 }}>
                  Sièges : <strong>{selectedZone.seats?.length || 0}</strong> · Capacité catégorie : <strong>{cats.find((c) => c.id === selectedZone.category_id)?.capacity || 0}</strong>
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal open={showAddZone} onClose={() => setShowAddZone(false)} title="Nouvelle zone" testId="add-zone-modal">
        <ZInput label="Nom (Carré Or, Balcon…)" value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} data-testid="add-zone-name" />
        <ZSelect label="Catégorie de billet" value={zoneForm.category_id} onChange={(e) => setZoneForm({ ...zoneForm, category_id: e.target.value })} data-testid="add-zone-cat">
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </ZSelect>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <ZInput label="X" type="number" value={zoneForm.x} onChange={(e) => setZoneForm({ ...zoneForm, x: e.target.value })} data-testid="add-zone-x" />
          <ZInput label="Y" type="number" value={zoneForm.y} onChange={(e) => setZoneForm({ ...zoneForm, y: e.target.value })} data-testid="add-zone-y" />
          <ZInput label="Largeur" type="number" value={zoneForm.width} onChange={(e) => setZoneForm({ ...zoneForm, width: e.target.value })} data-testid="add-zone-w" />
          <ZInput label="Hauteur" type="number" value={zoneForm.height} onChange={(e) => setZoneForm({ ...zoneForm, height: e.target.value })} data-testid="add-zone-h" />
        </div>
        {map.mode === "seats" && (
          <>
            <label className="z-label" style={{ fontSize: 13 }}>Générer une grille de sièges (optionnel)</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <ZInput label="Rangées" type="number" min="0" max="20" value={zoneForm.rows} onChange={(e) => setZoneForm({ ...zoneForm, rows: e.target.value })} data-testid="add-zone-rows" />
              <ZInput label="Places par rangée" type="number" min="0" max="30" value={zoneForm.cols} onChange={(e) => setZoneForm({ ...zoneForm, cols: e.target.value })} data-testid="add-zone-cols" />
            </div>
          </>
        )}
        {zoneErr && <p className="z-error-text">{zoneErr}</p>}
        <div style={{ display: "flex", gap: 10 }}>
          <ZButton variant="outline" block onClick={() => setShowAddZone(false)}>Annuler</ZButton>
          <ZButton block onClick={addZone} data-testid="add-zone-confirm-btn">Ajouter</ZButton>
        </div>
      </Modal>
    </div>
  );
}
